import { useEffect, useState } from 'react'

import { useBackfillCompletion } from '../../BackfillContext'
import { useCommunityConfig } from '../../CommunityConfig'
import type { Service, ChannelType } from '../../graphqlEnums'
import { isBackfillLoading } from '../../util/backfillCookies'
import { getIsRealtimeSync } from '../../util/channelSyncHelpers'

type BackfillCandidateChannel = {
  id: string
  service?: Service
  type?: ChannelType
}

/**
 * Returns the first channel with an active comments backfill — a loading
 * cookie, or an initiated/completed state — skipping realtime-sync channels.
 *
 * The backfill banner is per-channel, but the list view can show one or many
 * channels; keying off the single `channels[0]` misses backfills on other
 * channels (and shows nothing in the all-channels list). This picks whichever
 * channel is currently syncing so the banner appears in every list context.
 */
export function useActiveBackfillChannel<T extends BackfillCandidateChannel>(
  channels: readonly T[],
): T | undefined {
  const { hasChannelInitiated, hasChannelCompleted } = useBackfillCompletion()
  const { config } = useCommunityConfig()
  const [activeId, setActiveId] = useState<string | undefined>(undefined)

  useEffect(() => {
    const findActive = (): void => {
      const active = channels.find((channel) => {
        if (getIsRealtimeSync(channel, config)) {
          return false
        }
        return (
          isBackfillLoading(channel.id) ||
          hasChannelInitiated(channel.id) ||
          hasChannelCompleted(channel.id)
        )
      })
      setActiveId(active?.id)
    }

    findActive()
    const intervalId = setInterval(findActive, 3_000)
    return (): void => clearInterval(intervalId)
  }, [channels, config, hasChannelInitiated, hasChannelCompleted])

  return channels.find((channel) => channel.id === activeId)
}
